import React, { useEffect, useState } from "react"
import { InlineWidget } from "react-calendly"
import { useNavigate, useSearchParams } from "react-router-dom"
import HeaderSection from "../components/header-section"
import Sidebar from "../features/sidebar/components/sidebar"
import { trackOnboardingStep } from "../lib/analytics"

type OnboardingSuccessProps = {
  participantCount?: number
}

const OnboardingSuccess: React.FC<OnboardingSuccessProps> = ({
  participantCount = 5
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [showCalendar, setShowCalendar] = useState(true)

  useEffect(() => {
    trackOnboardingStep("success")
  }, [])

  // Get participant count from URL params, fallback to prop, then to default
  const actualParticipantCount = searchParams.get('count')
    ? parseInt(searchParams.get('count')!)
    : participantCount

  return (
    <div className="relative bg-base-white w-full h-[100dvh] flex flex-row items-start justify-start font-text-md-regular">
      <Sidebar />
      <div className="w-full h-[100dvh] flex-1 flex flex-col">
        <HeaderSection />
        <div className="bg-gray-50 relative box-border w-full max-w-[100dvw] h-full flex-1 flex flex-col justify-start py-6 px-9 gap-[19px] text-left text-sm text-gray-600 font-text-sm-medium border-r-[1px] border-solid border-gray-200 overflow-auto">

          <div className="w-full max-w-7xl mx-auto">
            {/* Success Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Success! Invitations Sent to {actualParticipantCount} Participants
              </h1>
              <p className="text-lg text-gray-600">
                Your team members will receive their invitations within the next 2 minutes.
              </p>
            </div>

            {/* Two Column Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Left Column - Timeline */}
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h2 className="text-2xl font-semibold text-gray-900 mb-6">What Happens Next:</h2>
                <div className="space-y-6">
                  {/* Right Now */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 7.89a2 2 0 002.83 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Right Now</h3>
                      <p className="text-gray-600">Email invitations delivered to your team</p>
                    </div>
                  </div>

                  {/* Next Week */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Next Week</h3>
                      <div className="text-gray-600 space-y-2">
                        <p>• Team members complete their 30-60 minute simulation</p>
                        <p>• Track completion progress in your dashboard</p>
                      </div>
                    </div>
                  </div>

                  {/* Once Everyone Completes */}
                  <div className="flex items-start">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Once Everyone Completes</h3>
                      <div className="text-gray-600 space-y-2">
                        <p>• Schedule your results review with our experts</p>
                        <p>• Receive comprehensive behavioral insights</p>
                        <p>• Get personalized team development recommendations</p>
                      </div>
                    </div>
                  </div>

                  {/* Important Note */}
                  <div className="flex items-start bg-amber-50 p-4 rounded-lg">
                    <div className="flex-shrink-0 w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">Important: Results are reviewed together with our experts</h3>
                      <p className="text-gray-700">This ensures you get maximum value from the insights</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Tips & Calendar */}
              <div className="space-y-6">
                {/* Tips Box */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h3 className="font-semibold text-blue-900 mb-3">
                    Tips for High Completion Rates
                  </h3>
                  <div className="text-blue-800 space-y-1 text-sm">
                    <p>• Send a team message explaining this is for development, not evaluation</p>
                    <p>• Emphasize it&apos;s engaging - like a game!</p>
                    <p>• Set a completion deadline (e.g., &quot;Please complete by Friday&quot;)</p>
                  </div>
                  <p className="text-blue-700 text-sm mt-4">
                    Need to add more participants? You can always do that from your dashboard.
                  </p>
                </div>

                {/* Schedule Results Review */}
                {showCalendar && (
                  <div className="bg-white rounded-xl shadow-lg p-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4 text-center">
                      Schedule Your Results Review
                    </h2>
                    <p className="text-gray-600 text-center mb-4 text-sm">
                      Choose your preferred time (you can reschedule anytime)
                    </p>

                    <div className="mb-4">
                      <InlineWidget
                        url="https://calendly.com/meseekna/data-review"
                        styles={{ height: "500px", minHeight: "500px" }}
                        pageSettings={{
                          hideEventTypeDetails: true,
                          hideLandingPageDetails: true,
                        }}
                      />
                    </div>

                    <div className="text-center">
                      <button
                        onClick={() => setShowCalendar(false)}
                        className="text-blue-600 hover:text-blue-800 underline text-sm"
                      >
                        I&apos;ll schedule this later
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-4 justify-center">
              <button
                onClick={() => navigate("/dashboard")}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Go to Dashboard
              </button>
              <button
                onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
                className="bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors"
              >
                Invite More People
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OnboardingSuccess